import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import type { ArInvoiceWithLines } from '@dcc-erp/shared';
import { AR_VOID_REASONS, fmt, fmtDate, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { PageHead } from '../layout';
import { CreateCreditNoteModal } from './CreateCreditNoteModal';
import { VoidInvoiceModal } from './VoidInvoiceModal';
import { useActiveCompanyId, useArInvoiceVoids } from '../../lib/queries';

// Disabled-void tooltip — voiding is reserved for a posted invoice that is still
// fully untouched (no payments, no credit notes); anything else must use a
// credit note instead.
const VOID_DISABLED_TIP = 'ยกเลิกได้เฉพาะใบที่ยังไม่จ่ายและไม่มีใบลดหนี้';

// Router-free invoice detail card. The route owns data fetching + params; this
// component owns presentation only (header + lines + totals + receipt-allocation
// timeline + JV reference). Plain prototype.css classes only.
//
// The detail wire shape (ar-invoices.service.findInvoice) includes the receipt
// allocations with the settling receipt's voucherNo/date, but the shared
// ArInvoiceWithLines type only declares `lines`. We narrow the prop type here to
// surface the allocations the API genuinely returns without touching the
// backend contract.
interface AllocationRow {
  id: string;
  amount: string | number;
  receipt: { voucherNo: string; receiptDate: string | Date };
}

export type InvoiceDetailData = ArInvoiceWithLines & {
  allocations?: AllocationRow[];
};

export interface InvoiceDetailProps {
  invoice: InvoiceDetailData;
}

const STATUS_BADGE: Record<string, { cls: string; label: string }> = {
  PAID: { cls: 'positive', label: 'ชำระแล้ว' },
  VOID: { cls: 'neutral', label: 'ยกเลิก' },
  POSTED: { cls: 'info', label: 'รอชำระ' },
};

export function InvoiceDetail({ invoice }: InvoiceDetailProps) {
  const total = toNumber(invoice.totalAmount);
  const paid = toNumber(invoice.paidAmount);
  const credited = toNumber(invoice.creditedAmount);
  // Decimal-safe subtraction: all operands are coerced to numbers first.
  // outstanding = total − paid − credited (Phase 2b; credited reads 0 today).
  const outstanding = total - paid - credited;

  const badge = STATUS_BADGE[invoice.status] ?? STATUS_BADGE.POSTED!;
  const allocations = invoice.allocations ?? [];

  const [cnOpen, setCnOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  // A credit note reduces a live receivable — only offer it on a POSTED invoice
  // that still carries outstanding (a VOID/fully-credited invoice has nothing
  // left to credit).
  const canCreateCreditNote = invoice.status === 'POSTED' && outstanding > 0;
  // Voiding cancels the whole invoice — only allowed on a POSTED invoice that is
  // still fully untouched (no payments, no credit notes ⇒ outstanding === total).
  const canVoid = invoice.status === 'POSTED' && paid === 0 && credited === 0;

  // Surface the latest void request for this invoice so the detail shows the
  // approval/void state (banner). A PENDING void → awaiting-approval cue; once
  // the invoice flips to VOID we link out to the reversing JV.
  const companyId = useActiveCompanyId();
  const { data: voids = [] } = useArInvoiceVoids(companyId, { invoiceId: invoice.id });
  const pendingVoid = voids.find((v) => v.approvalStatus === 'PENDING' || v.status === 'PENDING');
  // For a VOIDed invoice, the most recent POSTED void carries the reason + JV link.
  const postedVoid = voids.find((v) => v.status === 'POSTED');

  return (
    <>
      <PageHead
        crumbs={['ลูกหนี้การค้า', invoice.voucherNo]}
        title={`ใบแจ้งหนี้ ${invoice.voucherNo}`}
        actions={
          <>
            {canCreateCreditNote && (
              <button className="btn ghost" onClick={() => setCnOpen(true)}>
                <Icon name="ar" size={14} /> ออกใบลดหนี้
              </button>
            )}
            {invoice.status === 'POSTED' && (
              <button
                className="btn ghost"
                onClick={() => setVoidOpen(true)}
                disabled={!canVoid}
                title={canVoid ? undefined : VOID_DISABLED_TIP}
              >
                <Icon name="x" size={14} /> ยกเลิกใบแจ้งหนี้
              </button>
            )}
          </>
        }
      />

      {/* Void state banners */}
      {invoice.status === 'VOID' ? (
        <div
          style={{
            padding: '12px 14px',
            background: '#fdeaea',
            border: '1px solid var(--negative)',
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          <b style={{ color: 'var(--negative)' }}>ใบแจ้งหนี้นี้ถูกยกเลิกแล้ว</b>
          {postedVoid && (
            <span>
              {' '}
              — เหตุผล:{' '}
              {AR_VOID_REASONS[postedVoid.reasonCode as keyof typeof AR_VOID_REASONS] ??
                postedVoid.reasonCode}
              {postedVoid.reasonNote ? ` (${postedVoid.reasonNote})` : ''} · วันที่ยกเลิก{' '}
              {fmtDate(postedVoid.voidDate)}
              {postedVoid.journalEntryId && (
                <>
                  {' · '}
                  <Link
                    to="/journal"
                    search={{ jv: postedVoid.journalEntryId }}
                    style={{ color: 'var(--brand)', fontWeight: 500 }}
                  >
                    ดูใบสำคัญกลับรายการ (JV)
                  </Link>
                </>
              )}
            </span>
          )}
        </div>
      ) : pendingVoid ? (
        <div
          style={{
            padding: '10px 14px',
            background: '#fff7e6',
            border: '1px solid var(--warn, #e0a800)',
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          ⏳ ยกเลิกรออนุมัติ
        </div>
      ) : null}

      {/* Header block — customer / dates / status / branch */}
      <div className="card" style={{ padding: 18, marginBottom: 16 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 14,
          }}
        >
          <div>
            <div className="muted" style={{ fontSize: 12 }}>
              ลูกค้า
            </div>
            <div style={{ fontWeight: 600 }}>{invoice.customerName}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12 }}>
              สาขา
            </div>
            <div>{invoice.branchName ?? '—'}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12 }}>
              วันที่ออก
            </div>
            <div>{fmtDate(invoice.invoiceDate)}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12 }}>
              กำหนดชำระ
            </div>
            <div>{fmtDate(invoice.dueDate)}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12 }}>
              สถานะ
            </div>
            <div>
              <span className={`badge ${badge.cls}`}>
                <span className="bdot" />
                {badge.label}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Lines */}
      <div className="tbl-wrap" style={{ marginBottom: 16 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>คำอธิบาย</th>
              <th className="num">จำนวน</th>
              <th className="num">ราคา/หน่วย</th>
              <th>บัญชีรายได้</th>
              <th className="num">ยอดเงิน</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((l) => (
              <tr key={l.id}>
                <td className="num">{l.lineNo}</td>
                <td>{l.description}</td>
                <td className="num tnum">{fmt(toNumber(l.qty), 2)}</td>
                <td className="num tnum">{fmt(toNumber(l.unitPrice), 2)}</td>
                <td className="code">{l.revenueCode}</td>
                <td className="num tnum">{fmt(toNumber(l.amount), 2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div
        className="card"
        style={{
          padding: 18,
          marginBottom: 16,
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          rowGap: 6,
          maxWidth: 360,
          marginLeft: 'auto',
          fontSize: 14,
        }}
      >
        <div className="muted">ยอดก่อน VAT</div>
        <div className="tnum" style={{ textAlign: 'right' }}>
          {fmt(toNumber(invoice.subtotal), 2)}
        </div>
        <div className="muted">VAT ({fmt(toNumber(invoice.vatRate), 0)}%)</div>
        <div className="tnum" style={{ textAlign: 'right' }}>
          {fmt(toNumber(invoice.vatAmount), 2)}
        </div>
        <div style={{ fontWeight: 600 }}>รวมทั้งสิ้น</div>
        <div className="tnum" style={{ textAlign: 'right', fontWeight: 600 }}>
          {fmt(total, 2)}
        </div>
        <div className="muted">รับชำระแล้ว</div>
        <div className="tnum" style={{ textAlign: 'right' }}>
          {fmt(paid, 2)}
        </div>
        {credited > 0 && (
          <>
            <div className="muted">เครดิตแล้ว (ใบลดหนี้)</div>
            <div className="tnum" style={{ textAlign: 'right' }}>
              {fmt(credited, 2)}
            </div>
          </>
        )}
        <div style={{ fontWeight: 600 }}>คงค้าง</div>
        <div
          className={`tnum ${outstanding > 0 ? 'neg' : ''}`}
          style={{ textAlign: 'right', fontWeight: 600 }}
        >
          {fmt(outstanding, 2)}
        </div>
      </div>

      {/* Receipt-allocation timeline */}
      <div className="card" style={{ padding: 18, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>การรับชำระ</h3>
        {allocations.length === 0 ? (
          <div className="muted" style={{ fontSize: 14 }}>
            ยังไม่มีการรับชำระ
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>เลขที่ใบรับชำระ</th>
                <th>วันที่</th>
                <th className="num">จำนวนเงิน</th>
              </tr>
            </thead>
            <tbody>
              {allocations.map((a) => (
                <tr key={a.id}>
                  <td className="code">{a.receipt.voucherNo}</td>
                  <td>{fmtDate(a.receipt.receiptDate)}</td>
                  <td className="num tnum">{fmt(toNumber(a.amount), 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* JV reference */}
      {invoice.journalEntryId && (
        <div
          style={{
            padding: '10px 12px',
            background: '#e7f4ec',
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          <b>ใบสำคัญบัญชี (JV):</b>{' '}
          <span className="code">{invoice.journalEntryId.slice(0, 8)}…</span>
        </div>
      )}

      {cnOpen && <CreateCreditNoteModal invoice={invoice} onClose={() => setCnOpen(false)} />}
      <VoidInvoiceModal invoice={invoice} open={voidOpen} onClose={() => setVoidOpen(false)} />
    </>
  );
}
